import { ACTION_TYPE, JobBuilderContext } from "context/jobBuilderContext";
import {
  Alert,
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  Input,
  LinearProgress,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import PartyModeIcon from "@mui/icons-material/PartyMode";
import { STATES_AND_PROVINCES, createFile, getGenerationString } from "utils";
import {
  AUTURA_ASPHALT_LIGHT,
  AUTURA_ERROR,
  AUTURA_SUCCESS,
  AUTURA_WARNING,
} from "context/themeContext";
import {
  EntityTypes,
  FileType,
  FileUpload,
  PartialVehicle,
  StateOption,
  VRGeneration,
  VRVertex,
  convertPartialVehicleToVehicle,
} from "types";
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TOAST_TYPE, Toast } from "context/toastContext";

import { AppContext } from "context/appContext";
import { FileContext } from "context/fileUploadContext";
import Webcam from "react-webcam";
import { useTranslation } from "react-i18next";

export interface VRCameraDialogProps {
  open: boolean;
  handleClose: (error: string) => void;
}

enum CameraState {
  Loading = 0,
  Capturing = 1,
  Processing = 2,
  Validation = 3,
}

type SVGRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type Position = {
  top: number;
  left?: number;
  right?: number;
};

type VRField =
  | "year"
  | "generation"
  | "color"
  | "make"
  | "model"
  | "plateNumber"
  | "plateState";

const IMAGE_QUALITY = 0.8;
const LIST_ITEM_HEIGHT = 50;
const HEAD_HEIGHT = 70;
const FOOT_HEIGHT = 70;

export const VRCameraDialog = ({
  open,
  handleClose,
}: VRCameraDialogProps): ReactNode => {
  const { state: appState } = useContext(AppContext);
  const { dispatch } = useContext(JobBuilderContext);
  const theme = useTheme();
  const { t } = useTranslation();

  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const webcamRef = useRef<Webcam>(null);
  const { uploadFileWithVR, removeFile } = useContext(FileContext);
  const [vehicle, setVehicle] = useState<PartialVehicle>({});
  const [cameraState, setCameraState] = useState<CameraState>(
    CameraState.Loading,
  );
  const [fileWithMetdata, setFileWithMetdata] = useState<FileUpload | null>(
    null,
  );
  const [localImage, setLocalImage] = useState<string>("");
  const [vehicleVertices, setVehicleVertices] = useState<SVGRect>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [plateVertices, setPlateVertices] = useState<SVGRect>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [plateNumber, setPlateNumber] = useState<string>("");
  const [plateState, setPlateState] = useState<StateOption | null>(null);
  const [plateScore, setPlateScore] = useState<number>(0);
  const [vehicleScore, setVehicleScore] = useState<number>(0);
  const [make, setMake] = useState<string>("");
  const [model, setModel] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [color, setColor] = useState<string>("");
  const [vin, setVin] = useState<string>("");
  const [trim, setTrim] = useState<string>("");
  const [generation, setGeneration] = useState<VRGeneration | null>(null);
  const [error, setError] = useState<Toast>({
    type: TOAST_TYPE.ERROR,
    message: "",
  });
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>(
    { width: 0, height: 0 },
  );

  useLayoutEffect(() => {
    setImageSize({
      width: window.innerWidth,
      height: window.innerHeight - HEAD_HEIGHT - FOOT_HEIGHT,
    });
  }, []);

  useEffect(() => {
    if (cameraState === CameraState.Validation) {
      // close the screen once all the fields are accepted or rejected
      if (
        year ||
        generation ||
        color ||
        make ||
        model ||
        plateNumber ||
        plateState
      ) {
        return;
      }

      const updatedVehicle: PartialVehicle = { ...vehicle };
      // if the user accepts the make and model, throw in the trim if we have it
      if (updatedVehicle.make && updatedVehicle.model) {
        updatedVehicle.trim = trim;
      }

      // this is where VIN validation would go
      updatedVehicle.vin = vin; // should we do this?

      if (color) {
        updatedVehicle.color = appState.colors.find(
          (c) => c.trim().toLowerCase() === color.trim().toLowerCase(),
        );
      }
      setVehicle(updatedVehicle);
      dispatch({
        type: ACTION_TYPE.SET_VEHICLE,
        payload: convertPartialVehicleToVehicle(updatedVehicle),
      });
      handleClose("");
    }
  }, [
    year,
    generation,
    color,
    make,
    model,
    plateNumber,
    plateState,
    cameraState,
    handleClose,
  ]);

  useEffect(() => {
    if (cameraState === CameraState.Loading && webcamRef.current) {
      setCameraState(CameraState.Capturing);
    }

    // eslint-disable-next-line react-compiler/react-compiler
  }, [cameraState, webcamRef.current]);

  const headerText = useMemo(() => {
    switch (cameraState) {
      case CameraState.Loading:
        return t("camera.loading");
      case CameraState.Capturing:
        return t("camera.capturing");
      case CameraState.Processing:
        return t("camera.processing");
      case CameraState.Validation:
        return t("camera.validation");
      default:
        return "";
    }
  }, [cameraState, t]);

  const captureButtonText = useMemo(() => {
    if (cameraState === CameraState.Validation) {
      return t("camera.retakeButton");
    } else {
      return t("camera.captureButton");
    }
  }, [cameraState]);

  const vehicleString = useMemo(() => {
    let s = "";

    // vehicle fields get priority
    // year if we have it, otherwise generation
    if (vehicle?.year) {
      s += vehicle.year;
    } else if (year) {
      s += year;
    } else if (generation) {
      s += `${generation.start}-${generation.end}`;
    }
    // color
    if (vehicle?.color) {
      s += ` ${vehicle.color}`;
    } else if (color) {
      s += ` ${color}`;
    }
    // make
    if (vehicle?.make) {
      s += ` ${vehicle.make}`;
    } else if (make) {
      s += ` ${make}`;
    }
    // model
    if (vehicle?.model) {
      s += ` ${vehicle.model}`;
    } else if (model) {
      s += ` ${model}`;
    }
    return s.trim();
  }, [year, generation, color, make, model]);

  const plateString = useMemo(() => {
    let s = "";
    if (vehicle?.plate?.number) {
      s += vehicle.plate.number;
    } else if (plateNumber) {
      s += plateNumber;
    }
    if (vehicle?.plate?.state) {
      s += `-${vehicle.plate.state}`;
    } else if (plateState) {
      s += `-${plateState.stateCode}`;
    }
    return s.trim();
  }, [plateNumber, plateState]);

  const handleSnackClose = useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }
      setOpenSnackbar(false);
      setError({ type: TOAST_TYPE.ERROR, message: "" });
    },
    [],
  );

  const displayToast = useCallback((toast: Toast) => {
    setError(toast);
    setOpenSnackbar(true);
  }, []);

  const handleCapture = useCallback(async () => {
    if (cameraState === CameraState.Validation) {
      handleRetake();
      return;
    }
    if (!webcamRef.current) return;

    const imageSrc = webcamRef.current.getScreenshot({
      height: imageSize.height,
      width: imageSize.width,
    });
    if (imageSrc) {
      handleProcessing(imageSrc);
    } else {
      displayToast({
        message: t("errors.camera.capturing"),
        type: TOAST_TYPE.ERROR,
      });
      handleRetake();
    }
  }, [webcamRef, cameraState]);

  const handleProcessing = useCallback(async (imageSrc: string) => {
    setCameraState(CameraState.Processing);
    setLocalImage(imageSrc);

    const file = createFile({
      imageSrc,
      metadata: {
        createdByUserID: appState.user.id,
        tenantID: appState.tenant.id,
        entityType: EntityTypes.Job,
        entityID: 0,
        fileType: FileType.Image,
        data: {
          title: `vrImage-${Date.now()}.png`,
          description: `vrImage-${Date.now()}.png`,
        },
      },
      isUploaded: true,
    });
    setFileWithMetdata(file); // save the file for later if we need to remove it
    const vrData = await uploadFileWithVR(file, true);
    if (Object.keys(vrData).length > 0) {
      handleVRResults(vrData);
      setCameraState(CameraState.Validation);
    } else {
      displayToast({
        message: t("errors.camera.noVehicle"),
        type: TOAST_TYPE.ERROR,
      });
      removeFile(file);
      handleRetake();
      return;
    }
  }, []);

  const handleVRResults = useCallback((vrData: PartialVehicle): void => {
    if (vrData.boundingPoly && vrData?.score) {
      setVehicleVertices(
        formatVertices(vrData.boundingPoly.normalizedVertices),
      );
      setVehicleScore(vrData.score);
    }
    if (vrData.generation) {
      setGeneration(vrData.generation);
    }
    if (vrData.year) {
      setYear(`${vrData.year}`);
    }
    if (vrData.color) {
      setColor(vrData.color);
    }
    if (vrData.make) {
      setMake(vrData.make);
    }
    if (vrData.model) {
      setModel(vrData.model);
    }
    if (vrData.vin) {
      setVin(vrData.vin);
    }
    if (vrData.trim) {
      setTrim(vrData.trim);
    }

    if (vrData.plate?.boundingPoly && vrData?.plate?.score) {
      setPlateVertices(
        formatVertices(vrData.plate.boundingPoly.normalizedVertices),
      );
      setPlateScore(vrData.plate.score);
    }

    if (vrData.plate?.number) {
      setPlateNumber(vrData.plate.number);
    }

    if (vrData.plate?.state) {
      const option = STATES_AND_PROVINCES.find(
        (state) => state.label === vrData.plate?.state,
      );
      if (option) {
        setPlateState(option);
      }
    }
  }, []);

  const formatVertices = useCallback((vertices: VRVertex[]): SVGRect => {
    const minX = Math.min(...vertices.map((v) => v.x));
    const minY = Math.min(...vertices.map((v) => v.y));
    const maxX = Math.max(...vertices.map((v) => v.x));
    const maxY = Math.max(...vertices.map((v) => v.y));

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }, []);

  const handleOnMediaError = useCallback(() => {
    handleClose(t("errors.camera.loading"));
  }, [displayToast, t]);

  const handleOnMedia = useCallback(() => {
    setCameraState(CameraState.Capturing);
  }, []);

  const getPosition = useCallback(
    (field: VRField): Position => {
      const fields = [];
      if (year) {
        fields.push("year");
      } else if (generation) {
        fields.push("generation");
      }
      if (color) {
        fields.push("color");
      }
      if (make) {
        fields.push("make");
      }
      if (model) {
        fields.push("model");
      }
      if (plateNumber && isPhone) {
        fields.push("plateNumber");
      }
      if (plateState && isPhone) {
        fields.push("plateState");
      }

      const offsetY =
        fields.indexOf(field) * LIST_ITEM_HEIGHT +
        (fields.indexOf(field) * 2 + 4);

      // xs viwports have a different layout
      if (isPhone) {
        return {
          top: HEAD_HEIGHT + offsetY + offsetY / 2,
          left: 24,
          right: 24,
        };
      }

      switch (field) {
        case "year":
          return {
            top: vehicleVertices.y * imageSize.height + offsetY,
            right: vehicleVertices.x * imageSize.width + 2,
          };
        case "generation":
          return {
            top: vehicleVertices.y * imageSize.height + offsetY,
            right: vehicleVertices.x * imageSize.width + 2,
          };
        case "color":
          return {
            top: vehicleVertices.y * imageSize.height + offsetY,
            right: vehicleVertices.x * imageSize.width + 2,
          };
        case "make":
          return {
            top: vehicleVertices.y * imageSize.height + offsetY,
            right: vehicleVertices.x * imageSize.width + 2,
          };
        case "model":
          return {
            top: vehicleVertices.y * imageSize.height + offsetY,
            right: vehicleVertices.x * imageSize.width + 2,
          };
        case "plateNumber":
          return {
            top:
              (plateVertices.y + plateVertices.height) * imageSize.height + 4,
            right: plateVertices.x * imageSize.width - 2,
          };
        case "plateState":
          return {
            top:
              (plateVertices.y + plateVertices.height) * imageSize.height +
              LIST_ITEM_HEIGHT +
              8,
            right: plateVertices.x * imageSize.width - 4,
          };
        default:
          return { top: 0 };
      }
    },
    [
      isPhone,
      vehicleVertices,
      plateVertices,
      year,
      generation,
      color,
      make,
      model,
      plateNumber,
      plateState,
    ],
  );

  const handleAcceptField = useCallback(
    (field: string) => () => {
      switch (field) {
        case "year":
          setVehicle({ ...vehicle, year });
          setGeneration(null);
          setYear("");
          break;
        case "generation":
          setVehicle({ ...vehicle, year: getGenerationString(generation) });
          setGeneration(null);
          break;
        case "color":
          setVehicle({ ...vehicle, color });
          setColor("");
          break;
        case "make":
          setVehicle({ ...vehicle, make });
          setMake("");
          break;
        case "model":
          setVehicle({ ...vehicle, model });
          setModel("");
          break;
        case "plateNumber":
          if (vehicle?.plate) {
            setVehicle({
              ...vehicle,
              plate: { ...vehicle.plate, number: plateNumber },
            });
          } else {
            setVehicle({
              ...vehicle,
              plate: { number: plateNumber, state: "" },
            });
          }
          setPlateNumber("");
          break;
        case "plateState":
          if (plateState && vehicle?.plate) {
            setVehicle({
              ...vehicle,
              plate: { ...(vehicle?.plate || {}), state: plateState.stateCode },
            });
            setPlateState(null);
          } else if (plateState) {
            setVehicle({
              ...vehicle,
              plate: { number: "", state: plateState.stateCode },
            });
            setPlateState(null);
          }
          break;
        default:
          break;
      }
    },
    [
      year,
      generation,
      color,
      make,
      model,
      vehicle,
      vehicle?.plate,
      plateNumber,
      plateState,
    ],
  );

  const handleRejectField = useCallback(
    (field: string) => () => {
      switch (field) {
        case "year":
          setYear("");
          break;
        case "generation":
          setGeneration(null);
          break;
        case "color":
          setColor("");
          break;
        case "make":
          setMake("");
          break;
        case "model":
          setModel("");
          break;
        case "plateNumber":
          setPlateNumber("");
          break;
        case "plateState":
          setPlateState(null);
          break;
        default:
          break;
      }
    },
    [year, generation, color, make, model],
  );

  const handleRetake = useCallback(() => {
    setCameraState(CameraState.Loading);

    setLocalImage("");
    setVehicle({});
    if (fileWithMetdata) {
      removeFile(fileWithMetdata);
    }

    setYear("");
    setGeneration(null);
    setColor("");
    setMake("");
    setModel("");
    setVin("");
    setTrim("");
    setPlateNumber("");
    setPlateState(null);
    setVehicleScore(0);
    setPlateScore(0);
    setVehicleVertices({ x: 0, y: 0, width: 0, height: 0 });
    setPlateVertices({ x: 0, y: 0, width: 0, height: 0 });
  }, [fileWithMetdata]);

  const handleCancel = useCallback(() => {
    handleRetake();
    handleClose("");
  }, [handleRetake, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={() => handleClose("")}
      aria-label="Vehicle Recognition Camera"
      maxWidth={false}
      fullScreen
      sx={{ zIndex: 3001, backgroundColor: theme.palette.secondary.main }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.secondary.main,
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.secondary.main,
            height: HEAD_HEIGHT,
            display: "flex",
            width: "100%",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 3002,
          }}
        >
          <Typography
            variant={"h5"}
            sx={{
              color: theme.palette.secondary.contrastText,
              textAlign: "center",
              width: "100%",
            }}
          >
            {headerText}
          </Typography>
        </Box>
        <Box
          p={0}
          className={"camerabox"}
          height={imageSize.height}
          width={imageSize.width}
          sx={{ mt: `${HEAD_HEIGHT}px`, mb: `${FOOT_HEIGHT}px` }}
        >
          {cameraState < CameraState.Processing && (
            <Webcam
              ref={webcamRef}
              audio={false}
              imageSmoothing={false}
              disablePictureInPicture
              videoConstraints={{
                width: imageSize.width,
                height: imageSize.height,
                aspectRatio: imageSize.width / imageSize.height,
                facingMode: "enviornment",
              }}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: 70,
                bottom: 70,
                objectFit: "cover",
                objectPosition: "center",
                height: imageSize.height,
                width: imageSize.width,
              }}
              forceScreenshotSourceSize
              screenshotQuality={IMAGE_QUALITY}
              screenshotFormat="image/png"
              onUserMedia={handleOnMedia}
              onUserMediaError={handleOnMediaError}
            />
          )}
          {cameraState >= CameraState.Processing && (
            <Box position="relative" sx={{ m: 0 }}>
              <img
                src={localImage}
                height={imageSize.height}
                width={imageSize.width}
              />
              <Box position="absolute" top={70} left={0} bottom={70} right={0}>
                <svg height={imageSize.height} width={imageSize.width}>
                  {/* Draw a rectangle for the vehicle */}
                  <rect
                    x={vehicleVertices.x * imageSize.width}
                    y={vehicleVertices.y * imageSize.height}
                    width={vehicleVertices.width * imageSize.width}
                    height={vehicleVertices.height * imageSize.height}
                    stroke={
                      isPhone ? "transparent" : theme.palette.primary.main
                    }
                    strokeWidth={2}
                    fill="transparent"
                  />
                  {/* Draw a rectangle for the license plate */}
                  <rect
                    x={plateVertices.x * imageSize.width}
                    y={plateVertices.y * imageSize.height}
                    width={plateVertices.width * imageSize.width}
                    height={plateVertices.height * imageSize.height}
                    stroke={isPhone ? "transparent" : "magenta"}
                    strokeWidth={2}
                    fill="transparent"
                  />
                </svg>
                {cameraState === CameraState.Processing && (
                  <Box
                    position={"absolute"}
                    top={-70}
                    left={0}
                    right={0}
                    sx={{
                      flex: 1,
                      width: "100%",
                      display: "flex",
                      height: "100%",
                      justifyContent: "flex-end",
                    }}
                  >
                    <LinearProgress
                      variant="indeterminate"
                      sx={{ width: "100%", height: 8 }}
                    />
                  </Box>
                )}
                {!isPhone && (
                  <Box
                    position="absolute"
                    left={`${vehicleVertices.x * imageSize.width}`}
                    top={`${vehicleVertices.y * imageSize.height - 35}`}
                    sx={{ py: 1 }}
                  >
                    <Typography variant={"button"} color={"primary"}>
                      {vehicleString}
                    </Typography>
                  </Box>
                )}
                {!isPhone && (
                  <Box
                    position="absolute"
                    left={`${plateVertices.x * imageSize.width}px`}
                    top={`${plateVertices.y * imageSize.height - 35}`}
                    sx={{ py: 1 }}
                  >
                    <Typography
                      variant={"button"}
                      sx={{
                        color: "magenta",
                      }}
                    >
                      {plateString}
                    </Typography>
                  </Box>
                )}
                {/* Overlay fields for vehicle */}
                {year && (
                  <Box
                    position="absolute"
                    top={getPosition("year").top}
                    right={getPosition("year").right}
                    left={getPosition("year").left}
                  >
                    <OverlayField
                      onAcceptClick={handleAcceptField("year")}
                      onRejectClick={handleRejectField("year")}
                      field={"year"}
                      value={year}
                      score={vehicleScore}
                    />
                  </Box>
                )}
                {generation && !year && (
                  <Box
                    position="absolute"
                    top={getPosition("generation").top}
                    right={getPosition("generation").right}
                    left={getPosition("generation").left}
                  >
                    <OverlayField
                      onAcceptClick={handleAcceptField("generation")}
                      onRejectClick={handleRejectField("generation")}
                      field={"generation"}
                      value={generation.start + "-" + generation.end}
                      score={vehicleScore}
                    />
                  </Box>
                )}
                {color && (
                  <Box
                    position="absolute"
                    top={getPosition("color").top}
                    right={getPosition("color").right}
                    left={getPosition("color").left}
                  >
                    <OverlayField
                      onAcceptClick={handleAcceptField("color")}
                      onRejectClick={handleRejectField("color")}
                      field={"color"}
                      value={color}
                      score={vehicleScore}
                    />
                  </Box>
                )}
                {make && (
                  <Box
                    position="absolute"
                    top={getPosition("make").top}
                    right={getPosition("make").right}
                    left={getPosition("make").left}
                  >
                    <OverlayField
                      onAcceptClick={handleAcceptField("make")}
                      onRejectClick={handleRejectField("make")}
                      field={"make"}
                      value={make}
                      score={vehicleScore}
                    />
                  </Box>
                )}
                {model && (
                  <Box
                    position="absolute"
                    top={getPosition("model").top}
                    right={getPosition("model").right}
                    left={getPosition("model").left}
                  >
                    <OverlayField
                      onAcceptClick={handleAcceptField("model")}
                      onRejectClick={handleRejectField("model")}
                      field={"model"}
                      value={model}
                      score={vehicleScore}
                    />
                  </Box>
                )}
                {/* Overlay fields for plate */}
                {plateNumber && (
                  <Box
                    position="absolute"
                    top={getPosition("plateNumber").top}
                    right={getPosition("plateNumber").right}
                    left={getPosition("plateNumber").left}
                  >
                    <OverlayField
                      onAcceptClick={handleAcceptField("plateNumber")}
                      onRejectClick={handleRejectField("plateNumber")}
                      field={"plateNumber"}
                      value={plateNumber}
                      score={plateScore}
                    />
                  </Box>
                )}
                {plateState && (
                  <Box
                    position="absolute"
                    top={getPosition("plateState").top}
                    right={getPosition("plateState").right}
                    left={getPosition("plateState").left}
                  >
                    <OverlayField
                      onAcceptClick={handleAcceptField("plateState")}
                      onRejectClick={handleRejectField("plateState")}
                      field={"plateState"}
                      value={plateState.label}
                      score={plateScore}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
        <Stack
          direction="row"
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          sx={{
            height: FOOT_HEIGHT,
            width: "100%",
            p: 2,
            backgroundColor: theme.palette.secondary.main,
            display: "flex",
            alignItems: "center",
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 3002,
          }}
        >
          <Box
            flex={1}
            sx={{ width: "100%", justifyContent: "right", display: "flex" }}
          >
            <Button
              sx={{ flex: { xs: 1, lg: "none" }, mr: 1 }}
              color="error"
              variant={"contained"}
              endIcon={<CancelIcon />}
              fullWidth
              aria-label={t("cancel")}
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
          </Box>
          <Box
            flex={2}
            sx={{ width: "100%", justifyContent: "center", display: "flex" }}
          >
            <Button
              variant={"contained"}
              onClick={handleCapture}
              fullWidth
              color={
                cameraState === CameraState.Validation ? "primary" : "success"
              }
              disabled={
                cameraState === CameraState.Processing ||
                cameraState === CameraState.Loading
              }
              endIcon={
                cameraState === CameraState.Validation ? (
                  <PartyModeIcon />
                ) : (
                  <CameraAltIcon />
                )
              }
            >
              {captureButtonText}
            </Button>
          </Box>
        </Stack>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={handleSnackClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={error.type}
            variant="filled"
            sx={{ width: "100%", mb: 10, fontWeight: "bold" }}
          >
            {error.message}
          </Alert>
        </Snackbar>
      </Box>
    </Dialog>
  );
};

type OverlayFieldProps = {
  onAcceptClick: (field: string) => void;
  onRejectClick: () => void;
  setValue?: (value: string) => void;
  field: VRField;
  value: string;
  score: number;
};

const OverlayField = ({
  onAcceptClick,
  onRejectClick,
  setValue,
  field,
  value,
  score,
}: OverlayFieldProps): ReactNode => {
  const getColorForScore = (score: number): string => {
    if (score < 0.4) {
      return AUTURA_ERROR;
    } else if (score < 0.7) {
      return AUTURA_WARNING;
    } else {
      return AUTURA_SUCCESS;
    }
  };
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      p={1}
      height={50}
      sx={{
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        borderRadius: 5,
        border: "2px solid",
        borderColor: getColorForScore(score),
      }}
    >
      <IconButton
        onClick={onRejectClick}
        color={"error"}
        sx={{ p: 1, "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.5)" } }}
      >
        <CloseIcon fontSize="large" />
      </IconButton>
      <Divider
        orientation="vertical"
        flexItem
        sx={{ borderColor: AUTURA_ASPHALT_LIGHT, borderOpacity: 0.5 }}
      />
      <Input
        value={value}
        onChange={(e) => setValue && setValue(e.target.value)}
        size="small"
        sx={{ width: 100, backgroundColor: "transparent", mb: -1 }}
        inputProps={{
          style: {
            color: "white",
            textAlign: "center",
            textTransform: "uppercase",
          },
        }}
      />
      <Divider
        orientation="vertical"
        flexItem
        sx={{ borderColor: AUTURA_ASPHALT_LIGHT, borderOpacity: 0.5 }}
      />
      <IconButton
        onClick={() => onAcceptClick(field)}
        color={"success"}
        sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.5)" } }}
      >
        <CheckCircleIcon fontSize="large" />
      </IconButton>
    </Box>
  );
};

export default VRCameraDialog;
